import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { isLoggedIn, isMobile, device } from "../../helpers";
import Logo from "./Logo";
import TopMenu from "./TopMenu";
import Search from "./Search";
import { FormattedMessage } from "react-intl";
import { getURLParam, RouteLocation } from "../../lib/routing";
import { AppState } from "../../store";

const Container = styled.div`
    position: fixed;
    z-index: 1000;
    top: 0;
    overflow: hidden;
    height: 100px;
    color: #000;
    background-color: #fafafa;
    border-bottom: 1px solid #ebebeb;

    display: flex;
    flex-wrap: wrap;
    width: 100%;
    padding: 0 30px 15px;

    @media ${device.phone} {
        padding: 14px 15px 7px 15px;
        height: 136px;
    } ;
`;

const Main = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-end;
    width: 100%;

    @media ${device.phone} {
        flex-direction: column;
        align-items: flex-start;
    } ;
`;

const Actions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;

    @media ${device.phone} {
        flex-direction: row-reverse;
        justify-content: center;
        width: 100%;
        margin-top: 14px;
    } ;
`;

const ButtonBordered = styled(Link)`
    user-select: none;
    cursor: pointer;
    padding: 0 16px;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: #999;
    height: 33px;
    line-height: 31px;
    text-decoration: none;
    transition: all 0.2s ease-in-out;

    &:hover {
        color: #000;
        border-color: #000;
    }

    span {
        font-family: Cairo, QuranFont, urdu, sans-serif;
    }
`;

const ActionButton = styled(Link)`
    user-select: none;
    cursor: pointer;
    padding: 0 16px;
    border-radius: 2px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: #fff;
    background-color: #34a962;
    height: 33px;
    line-height: 33px;
    text-decoration: none;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);

    &:hover {
        background-color: #37a866 !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }

    span {
        font-family: Cairo, QuranFont, urdu, sans-serif;
    }

    @media ${device.phone} {
        margin-left: 15px;
    } ;
`;

const Spacer = styled.div`
    position: relative;
    width: 20px;
    height: 42px;

    @media ${device.phone} {
        display: none;
    } ;
`;

const hidePaths = ["/app/r-", "/app/e/", "/app/merge/", "/app/pdf/"];
const hideMobilePaths = [
    "/app/login",
    "/app/docs",
    "/app/doc/create",
    "/app/doc/edit",
    "/app/doc/meta",
    "/app/pages",
    "/app/page/edit",
    "/app/users",
];

function hideHeader(path: string, isLoggedIn: boolean) {
    if (!isLoggedIn && path.startsWith("/b-170-")) {
        return true;
    }

    for (let i = 0; i < hidePaths.length; i += 1) {
        if (path.startsWith(hidePaths[i])) {
            return true;
        }
    }

    if (isMobile()) {
        for (let i = 0; i < hideMobilePaths.length; i += 1) {
            if (path.startsWith(hideMobilePaths[i])) {
                return true;
            }
        }

        const fromFB = !!getURLParam("fbclid");
        if (path.startsWith("/b-") && fromFB) {
            return true;
        }
    }

    return false;
}

const mapStateToProps = (state: AppState) => ({ user: state.user });
type StateProps = ReturnType<typeof mapStateToProps>;
type OwnProps = {
    location: RouteLocation;
};
type Props = StateProps & OwnProps;

class Header extends React.Component<Props> {
    primaryBtn() {
        if (!isLoggedIn(this.props.user)) {
            return (
                <ActionButton to="/app/login?tab=register">
                    <FormattedMessage id="header.loginRegister" />
                </ActionButton>
            );
        }

        return (
            <ActionButton to="/app/profile">
                <FormattedMessage id="header.account" />
            </ActionButton>
        );
    }

    secondaryBtn() {
        if (!isLoggedIn(this.props.user)) {
            return (
                <ButtonBordered to="/app/doc/create">
                    <FormattedMessage id="header.writeBook" />
                </ButtonBordered>
            );
        }

        return (
            <ButtonBordered to="/app/docs">
                <FormattedMessage id="header.docs" />
            </ButtonBordered>
        );
    }

    render() {
        const { location } = this.props;
        if (hideHeader(location.pathname, isLoggedIn(this.props.user))) {
            return null;
        }

        return (
            <Container>
                <TopMenu />
                <Main>
                    <Logo />
                    <Search location={location} />

                    <Actions>
                        {this.secondaryBtn()}
                        <Spacer />
                        {this.primaryBtn()}
                    </Actions>
                </Main>
            </Container>
        );
    }
}

export default connect(mapStateToProps)(Header);
